import React, { useState, useEffect } from "react";
import { useQuery, useAction } from "wasp/client/operations";
import { type AuthUser } from "wasp/auth";
import {
  getUsersWithPermissions,
  grantPermissions,
  revokePermissions,
  getMyPermissions,
  getPaginatedUsers,
  getLocationAdminsAndManagers,
  getAllLocationsForPermissions,
} from "wasp/client/operations";
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
} from "../../components/ui/card";
import { Button } from "../../components/ui/button";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../../components/ui/select";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { Checkbox } from "../../components/ui/checkbox";
import { Alert, AlertDescription } from "../../components/ui/alert";
import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from "../../components/ui/tabs";
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
  SheetTrigger,
} from "../../components/ui/sheet";
import {
  Loader2,
  Users,
  Shield,
  Plus,
  Trash2,
  CheckCircle,
  XCircle,
  Search,
  Building,
  UserCheck,
  Edit,
  X,
  UserPlus,
  Save,
  Mail,
  Award,
} from "lucide-react";
import useDebounce from "../../client/hooks/useDebounce";
import { useToast } from "../../hooks/use-toast";
import DefaultLayout from "../layout/DefaultLayout";
import { useLocationContext } from "../../client/contexts/LocationContext";
import {
  EnhancedActionButton,
  enhancedPageLayoutStyles,
  useResponsiveLayout,
  ResponsiveViewSwitcher,
} from "../../components/ui/enhanced-page-layout";
import {
  AdminFlyoutPane,
  FlyoutSection,
  FlyoutField,
  FlyoutActions,
} from "../components/AdminFlyoutPane";
import AdminTableLayout from "../components/AdminTableLayout";
import { TableFilterLayout } from "../../components/ui/table-filter-layout";

interface Permission {
  user: {
    id: string;
    email: string | null;
    username: string | null;
  };
  permissions: string[];
}

interface LocationPermission {
  location: {
    id: string;
    name: string;
  };
  contentType: string;
  permissions: string[];
}

interface User {
  id: string;
  email: string | null;
  username: string | null;
  isAdmin: boolean;
}

const PERMISSION_TYPES = [
  "CREATE",
  "READ",
  "UPDATE",
  "DELETE",
  "APPROVE",
  "MODERATE",
  "PIN",
  "ARCHIVE",
];

const CONTENT_TYPES = ["FACILITY", "NOTICE", "TENANT_BUSINESS", "EVENT"];

const MANAGEMENT_AREAS = [
  { value: "FACILITY", label: "Facility Management" },
  { value: "NOTICE", label: "Notice Management" },
  { value: "TENANT_BUSINESS", label: "Tenant Management" },
  { value: "EVENT", label: "Event Management" },
];

type RoleType = "ADMIN" | "MANAGER";

export default function PermissionManagementPage({ user }: { user: AuthUser }) {
  const { toast } = useToast();
  const { selectedLocation: contextLocation } = useLocationContext();

  // Use responsive layout hook
  const {
    viewMode,
    setViewMode,
    isFilterExpanded,
    setIsFilterExpanded,
    isMobile,
    shouldCollapseSidebar,
    hideViewSwitcher,
  } = useResponsiveLayout("table");

  const [selectedLocation, setSelectedLocationState] =
    React.useState<string>("");

  // Header (approx 6rem) + TableFilterLayout Columns (approx 2.5rem)
  // Collapsed: ~8.5rem + 1rem breathing room = 9.5rem
  // Expanded: ~8.5rem + Filters (~4rem) - adjusted to remove gap
  const contentPaddingTop = selectedLocation
    ? isFilterExpanded
      ? "pt-[1rem] md:pt-[12.5rem]"
      : "pt-[1rem] md:pt-[9.5rem]"
    : "pt-[calc(var(--header-height)+6rem)]";

  // Role Type Selection (for inline editing)
  const [selectedRoleType, setSelectedRoleType] = useState<RoleType | null>(
    null,
  );

  // Management Areas (for Location Manager)
  const [selectedManagementAreas, setSelectedManagementAreas] = useState<
    string[]
  >([]);

  // User Search and Selection
  const [userSearchQuery, setUserSearchQuery] = useState<string>("");
  const [selectedUsers, setSelectedUsers] = useState<User[]>([]);
  const [isGranting, setIsGranting] = useState(false);
  const [isRevoking, setIsRevoking] = useState(false);
  const [isFlyoutOpen, setIsFlyoutOpen] = useState(false);

  // New inline grid state
  const [gridUsers, setGridUsers] = useState<
    Array<{
      user: User;
      role: "ADMIN" | "MANAGER";
      permissions: {
        FACILITY: boolean;
        NOTICE: boolean;
        EVENT: boolean;
        TENANT_BUSINESS: boolean;
      };
    }>
  >([]);
  const [isSaving, setIsSaving] = useState(false);
  const [isAddingUser, setIsAddingUser] = useState(false);
  const [newUserSearch, setNewUserSearch] = useState<string>("");
  const [isFlyoutDialogOpen, setIsFlyoutDialogOpen] = useState(false);
  const [selectedUsersForFlyout, setSelectedUsersForFlyout] = useState<User[]>(
    [],
  );
  const [editingUser, setEditingUser] = useState<string | null>(null);
  const [editingUserData, setEditingUserData] = useState<{
    role: "ADMIN" | "MANAGER";
    permissions: {
      FACILITY: boolean;
      NOTICE: boolean;
      EVENT: boolean;
      TENANT_BUSINESS: boolean;
    };
  } | null>(null);
  const [displaySearch, setDisplaySearch] = useState<string>("");

  // Flyout state
  const [flyoutUserId, setFlyoutUserId] = useState<string | null>(null);
  const [flyoutMode, setFlyoutMode] = useState<"view" | "edit">("view");

  const debouncedUserSearch = useDebounce(userSearchQuery, 300);
  const debouncedNewUserSearch = useDebounce(newUserSearch, 300);

  // Initialize selectedLocation from context
  useEffect(() => {
    if (contextLocation?.id) {
      setSelectedLocationState(contextLocation.id);
    }
  }, [contextLocation?.id]);

  // Get current user's permissions to determine which locations they can manage
  const {
    data: myPermissions,
    isLoading: loadingMyPermissions,
    error: myPermissionsError,
  } = useQuery(getMyPermissions);

  // Get all locations (for admin users)
  const {
    data: allLocations,
    isLoading: loadingAllLocations,
    error: allLocationsError,
  } = useQuery(getAllLocationsForPermissions);

  // Get users with permissions for selected location (for existing admins/managers display)
  const {
    data: locationAdminsAndManagers,
    isLoading: loadingUsers,
    refetch: refetchUsers,
    error: locationUsersError,
  } = useQuery(
    getLocationAdminsAndManagers,
    {
      locationId: selectedLocation,
    },
    {
      enabled: !!selectedLocation,
    },
  );

  // Navigation helpers for flyout
  const getUsersForFlyout = () => {
    const userPermissions = new Map();
    locationAdminsAndManagers?.forEach((permission) => {
      const userId = permission.user.id;
      if (!userPermissions.has(userId)) {
        userPermissions.set(userId, {
          user: permission.user,
          permissions: new Set(),
          contentTypes: new Set(),
        });
      }
      userPermissions.get(userId).contentTypes.add(permission.contentType);
      permission.permissions.forEach((perm) => {
        userPermissions.get(userId).permissions.add(perm);
      });
    });
    return Array.from(userPermissions.values());
  };

  const getUsersListWithGrid = () => {
    return [
      ...gridUsers.map((gu) => ({ user: gu.user })),
      ...getUsersForFlyout(),
    ];
  };

  const currentUserIndex =
    getUsersListWithGrid().findIndex(
      (item: any) => item.user.id === flyoutUserId,
    ) ?? -1;
  const hasPreviousUser = currentUserIndex > 0;
  const hasNextUser =
    currentUserIndex >= 0 &&
    currentUserIndex < (getUsersListWithGrid().length ?? 0) - 1;

  const handleNavigatePreviousUser = () => {
    if (hasPreviousUser) {
      const usersList = getUsersListWithGrid();
      const previousUser = usersList[currentUserIndex - 1];
      setFlyoutUserId(previousUser.user.id);
      setFlyoutMode("view");
    }
  };

  const handleNavigateNextUser = () => {
    if (hasNextUser) {
      const usersList = getUsersListWithGrid();
      const nextUser = usersList[currentUserIndex + 1];
      setFlyoutUserId(nextUser.user.id);
      setFlyoutMode("view");
    }
  };

  // Search for users to assign roles
  const { data: userSearchResults, isLoading: searchingUsers } = useQuery(
    getPaginatedUsers,
    {
      skipPages: 0,
      pageSize: 25,
      filter: {
        ...(debouncedUserSearch && { emailContains: debouncedUserSearch }),
        isAdmin: undefined, // Don't filter by admin status
      },
    },
    {
      enabled: !!debouncedUserSearch && debouncedUserSearch.length >= 2,
    },
  );

  // Search for new users to add
  const { data: newUserSearchResults, isLoading: searchingNewUsers } = useQuery(
    getPaginatedUsers,
    {
      skipPages: 0,
      pageSize: 25,
      filter: {
        ...(debouncedNewUserSearch && {
          emailContains: debouncedNewUserSearch,
        }),
        isAdmin: undefined, // Don't filter by admin status
      },
    },
    {
      enabled: !!debouncedNewUserSearch && debouncedNewUserSearch.length >= 2,
    },
  );

  const grantPermissionsAction = useAction(grantPermissions);
  const revokePermissionsAction = useAction(revokePermissions);

  // Use all locations if available, otherwise fall back to permissions-based locations
  const availableLocations =
    allLocations || myPermissions?.map((p) => p.location) || [];

  // Debug logging

  const uniqueLocations = Array.isArray(availableLocations)
    ? availableLocations.filter((location, index, self) => {
      const locationObj = location as any;
      return (
        locationObj &&
        locationObj.id &&
        index ===
        self.findIndex((l) => {
          const lObj = l as any;
          return lObj && lObj.id === locationObj.id;
        })
      );
    })
    : [];

  const handleGrantRole = async () => {
    if (selectedUsers.length === 0 || !selectedLocation || !selectedRoleType) {
      toast({
        title: "Missing Information",
        description: "Please select users, location, and role type",
        variant: "destructive",
      });
      return;
    }

    if (
      selectedRoleType === "MANAGER" &&
      selectedManagementAreas.length === 0
    ) {
      toast({
        title: "Missing Management Areas",
        description:
          "Please select at least one management area for Location Manager",
        variant: "destructive",
      });
      return;
    }

    setIsGranting(true);
    try {
      for (const user of selectedUsers) {
        if (selectedRoleType === "ADMIN") {
          // Grant full permissions for all content types
          for (const contentType of CONTENT_TYPES) {
            await grantPermissionsAction({
              userId: user.id,
              locationId: selectedLocation,
              contentType: contentType as any,
              permissions: PERMISSION_TYPES as any,
            });
          }
        } else if (selectedRoleType === "MANAGER") {
          // Grant permissions for selected management areas
          for (const contentType of selectedManagementAreas) {
            await grantPermissionsAction({
              userId: user.id,
              locationId: selectedLocation,
              contentType: contentType as any,
              permissions: PERMISSION_TYPES as any,
            });
          }
        }
      }

      setSelectedUsers([]);
      setUserSearchQuery("");
      setSelectedManagementAreas([]);
      setIsFlyoutOpen(false);
      refetchUsers();
      toast({
        title: "Success",
        description: `${selectedRoleType === "ADMIN"
            ? "Location Administrator"
            : "Location Manager"
          } role granted successfully to ${selectedUsers.length} user(s)`,
      });
    } catch (error) {
      console.error("Error granting role:", error);
      toast({
        title: "Error",
        description: "Failed to grant role",
        variant: "destructive",
      });
    } finally {
      setIsGranting(false);
    }
  };

  const handleRevokePermissions = async (userId: string) => {
    if (!window.confirm("Are you sure you want to revoke all permissions for this user? This action cannot be undone.")) {
      return;
    }
    setIsRevoking(true);
    try {
      // Revoke permissions for all content types
      for (const contentType of CONTENT_TYPES) {
        await revokePermissionsAction({
          userId,
          locationId: selectedLocation,
          contentType: contentType as any,
        });
      }

      refetchUsers();
      toast({
        title: "Success",
        description: "Permissions revoked successfully",
      });
    } catch (error) {
      console.error("Error revoking permissions:", error);
      toast({
        title: "Error",
        description: "Failed to revoke permissions",
        variant: "destructive",
      });
    } finally {
      setIsRevoking(false);
    }
  };

  const toggleManagementArea = (area: string) => {
    setSelectedManagementAreas((prev) =>
      prev.includes(area) ? prev.filter((a) => a !== area) : [...prev, area],
    );
  };

  const toggleUserSelection = (user: User) => {
    setSelectedUsers((prev) =>
      prev.some((u) => u.id === user.id)
        ? prev.filter((u) => u.id !== user.id)
        : [...prev, user],
    );
  };

  const removeSelectedUser = (userId: string) => {
    setSelectedUsers((prev) => prev.filter((u) => u.id !== userId));
  };

  const addUserToGrid = (user: User) => {
    // Check if user already exists in grid
    if (gridUsers.some((gridUser) => gridUser.user.id === user.id)) {
      return;
    }

    setGridUsers((prev) => [
      ...prev,
      {
        user,
        role: "MANAGER",
        permissions: {
          FACILITY: false,
          NOTICE: false,
          EVENT: false,
          TENANT_BUSINESS: false,
        },
      },
    ]);
  };

  const startAddingUser = () => {
    setIsAddingUser(true);
    setNewUserSearch("");
  };

  const cancelAddingUser = () => {
    setIsAddingUser(false);
    setNewUserSearch("");
  };

  const selectNewUser = (user: User) => {
    addUserToGrid(user);
    setIsAddingUser(false);
    setNewUserSearch("");
  };

  const toggleUserSelectionInFlyout = (user: User) => {
    setSelectedUsersForFlyout((prev) =>
      prev.some((u) => u.id === user.id)
        ? prev.filter((u) => u.id !== user.id)
        : [...prev, user],
    );
  };

  const addSelectedUsersToGrid = () => {
    if (selectedUsersForFlyout.length === 0) {
      toast({
        title: "No Users Selected",
        description: "Please select at least one user to add",
        variant: "destructive",
      });
      return;
    }

    const count = selectedUsersForFlyout.length;
    selectedUsersForFlyout.forEach((user) => {
      addUserToGrid(user);
    });
    setSelectedUsersForFlyout([]);
    setIsFlyoutDialogOpen(false);
    setNewUserSearch("");

    toast({
      title: "Users Added",
      description: `${count} user(s) added. Please select their role and permissions, then click Save.`,
    });
  };

  const openFlyoutDialog = () => {
    setIsFlyoutDialogOpen(true);
    setSelectedUsersForFlyout([]);
    setNewUserSearch("");
  };

  const startEditingUser = (userId: string, userData: any) => {
    setEditingUser(userId);
    const isAdmin =
      userData.contentTypes.size === 4 && userData.permissions.has("APPROVE");
    setEditingUserData({
      role: isAdmin ? "ADMIN" : "MANAGER",
      permissions: {
        FACILITY: userData.contentTypes.has("FACILITY"),
        NOTICE: userData.contentTypes.has("NOTICE"),
        EVENT: userData.contentTypes.has("EVENT"),
        TENANT_BUSINESS: userData.contentTypes.has("TENANT_BUSINESS"),
      },
    });
  };

  const cancelEditingUser = () => {
    setEditingUser(null);
    setEditingUserData(null);
  };

  const saveUserEdit = async () => {
    if (!editingUser || !editingUserData) return;

    setIsSaving(true);
    try {
      if (editingUserData.role === "ADMIN") {
        // Grant full permissions for all content types
        for (const contentType of CONTENT_TYPES) {
          await grantPermissionsAction({
            userId: editingUser,
            locationId: selectedLocation,
            contentType: contentType as any,
            permissions: PERMISSION_TYPES as any,
          });
        }
      } else {
        // First revoke all permissions
        for (const contentType of CONTENT_TYPES) {
          await revokePermissionsAction({
            userId: editingUser,
            locationId: selectedLocation,
            contentType: contentType as any,
          });
        }
        // Then grant permissions for selected areas only
        for (const [contentType, hasPermission] of Object.entries(
          editingUserData.permissions,
        )) {
          if (hasPermission) {
            await grantPermissionsAction({
              userId: editingUser,
              locationId: selectedLocation,
              contentType: contentType as any,
              permissions: PERMISSION_TYPES as any,
            });
          }
        }
      }

      setEditingUser(null);
      setEditingUserData(null);
      refetchUsers();
      toast({
        title: "Success",
        description: "User permissions updated successfully",
      });
    } catch (error) {
      console.error("Error updating user permissions:", error);
      toast({
        title: "Error",
        description: "Failed to update user permissions",
        variant: "destructive",
      });
    } finally {
      setIsSaving(false);
    }
  };

  const removeUserFromGrid = (userId: string) => {
    setGridUsers((prev) =>
      prev.filter((gridUser) => gridUser.user.id !== userId),
    );
  };

  const updateUserRole = (userId: string, role: "ADMIN" | "MANAGER") => {
    setGridUsers((prev) =>
      prev.map((gridUser) => {
        if (gridUser.user.id === userId) {
          const newPermissions =
            role === "ADMIN"
              ? {
                FACILITY: true,
                NOTICE: true,
                EVENT: true,
                TENANT_BUSINESS: true,
              }
              : {
                FACILITY: false,
                NOTICE: false,
                EVENT: false,
                TENANT_BUSINESS: false,
              };

          return { ...gridUser, role, permissions: newPermissions };
        }
        return gridUser;
      }),
    );
  };

  const updateUserPermission = (
    userId: string,
    permissionType: keyof (typeof gridUsers)[0]["permissions"],
    value: boolean,
  ) => {
    setGridUsers((prev) =>
      prev.map((gridUser) => {
        if (gridUser.user.id === userId) {
          const newPermissions = {
            ...gridUser.permissions,
            [permissionType]: value,
          };

          // Check if all permissions are selected - if so, switch to ADMIN
          const allSelected = Object.values(newPermissions).every(Boolean);
          const newRole = allSelected ? "ADMIN" : "MANAGER";

          return {
            ...gridUser,
            permissions: newPermissions,
            role: newRole,
          };
        }
        return gridUser;
      }),
    );
  };

  const handleSaveAllPermissions = async () => {
    if (gridUsers.length === 0) {
      toast({
        title: "No Users",
        description: "No users to save",
        variant: "destructive",
      });
      return;
    }

    // Validate that MANAGER users have at least one permission area selected
    const invalidUsers = gridUsers.filter(
      (gridUser) =>
        gridUser.role === "MANAGER" &&
        !Object.values(gridUser.permissions).some(Boolean),
    );

    if (invalidUsers.length > 0) {
      toast({
        title: "Missing Permissions",
        description: `Please select at least one management area for ${invalidUsers.length} user(s) with Manager role`,
        variant: "destructive",
      });
      return;
    }

    setIsSaving(true);
    try {
      const savedCount = gridUsers.length;
      const savedUserIds: string[] = [];

      for (const gridUser of gridUsers) {
        if (gridUser.role === "ADMIN") {
          // Grant full permissions for all content types
          for (const contentType of CONTENT_TYPES) {
            const result = await grantPermissionsAction({
              userId: gridUser.user.id,
              locationId: selectedLocation,
              contentType: contentType as any,
              permissions: PERMISSION_TYPES as any,
            });
          }
          savedUserIds.push(gridUser.user.id);
        } else {
          // For MANAGER: first revoke all permissions, then grant only selected areas
          // This ensures clean state
          for (const contentType of CONTENT_TYPES) {
            try {
              await revokePermissionsAction({
                userId: gridUser.user.id,
                locationId: selectedLocation,
                contentType: contentType as any,
              });
            } catch (err) {
              // Ignore errors if permissions don't exist yet
            }
          }

          // Then grant permissions for selected areas only
          let hasAnyPermissions = false;
          for (const [contentType, hasPermission] of Object.entries(
            gridUser.permissions,
          )) {
            if (hasPermission) {
              const result = await grantPermissionsAction({
                userId: gridUser.user.id,
                locationId: selectedLocation,
                contentType: contentType as any,
                permissions: PERMISSION_TYPES as any,
              });

              hasAnyPermissions = true;
            }
          }

          if (hasAnyPermissions) {
            savedUserIds.push(gridUser.user.id);
          }
        }
      }

      setGridUsers([]);

      // Wait a bit before refetching to ensure database is updated
      await new Promise((resolve) => setTimeout(resolve, 1000));

      const refetchResult = await refetchUsers();

      toast({
        title: "Success",
        description: `Permissions saved successfully for ${savedCount} user(s)`,
      });
    } catch (error) {
      console.error("Error saving permissions:", error);
      toast({
        title: "Error",
        description:
          error instanceof Error ? error.message : "Failed to save permissions",
        variant: "destructive",
      });
    } finally {
      setIsSaving(false);
    }
  };

  const resetWorkflow = () => {
    setSelectedRoleType(null);
    setSelectedManagementAreas([]);
    setUserSearchQuery("");
    setSelectedUsers([]);
    setGridUsers([]);
    setIsFlyoutOpen(false);
    setIsAddingUser(false);
    setNewUserSearch("");
    setIsFlyoutDialogOpen(false);
    setSelectedUsersForFlyout([]);
    setEditingUser(null);
    setEditingUserData(null);
  };

  if (loadingMyPermissions || loadingAllLocations) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <Loader2 className="h-8 w-8 animate-spin" />
      </div>
    );
  }

  // Show error if queries fail
  if (myPermissionsError || allLocationsError) {
    return (
      <DefaultLayout user={user}>
        <div className="flex items-center justify-center min-h-screen">
          <Alert variant="destructive" className="max-w-md">
            <AlertDescription>
              <p className="font-semibold mb-2">
                Error loading permissions data
              </p>
              <p className="text-sm">
                {myPermissionsError?.message ||
                  allLocationsError?.message ||
                  "An unknown error occurred"}
              </p>
              <Button
                variant="outline"
                className="mt-4"
                onClick={() => window.location.reload()}
              >
                Refresh Page
              </Button>
            </AlertDescription>
          </Alert>
        </div>
      </DefaultLayout>
    );
  }

  return (
    <DefaultLayout user={user}>
      <div className="w-full">
        {/* Fixed Header */}
        <div className="fixed top-[var(--header-height)] left-[var(--sidebar-width)] right-0 z-20 bg-white dark:bg-sidebar-active transition-colors">
          <div className="px-4 py-3">
            <div className="flex items-center justify-between">
              <div>
                <h1 className="text-2xl font-bold text-gray-900">
                  Permission Management
                </h1>
                <p className="text-sm text-gray-600 mt-1">
                  Assign Location Administrator or Location Manager roles to
                  users
                </p>
              </div>
              <div className="flex items-center gap-3">
                {gridUsers.length > 0 && (
                  <Button
                    onClick={handleSaveAllPermissions}
                    disabled={isSaving}
                    className="w-full sm:w-auto bg-blue-600 hover:bg-blue-700 text-white"
                  >
                    {isSaving ? (
                      <>
                        <Loader2 className="mr-2 h-4 w-4 animate-spin text-white" />
                        <span className="text-white">Saving...</span>
                      </>
                    ) : (
                      <>
                        <Save className="mr-2 h-4 w-4 text-white" />
                        <span className="text-white">Save</span>
                      </>
                    )}
                  </Button>
                )}
                <Sheet
                  open={isFlyoutDialogOpen}
                  onOpenChange={setIsFlyoutDialogOpen}
                >
                  <SheetTrigger asChild>
                    <EnhancedActionButton
                      onClick={openFlyoutDialog}
                      icon={Plus}
                      label="Add User"
                    />
                  </SheetTrigger>
                  <SheetContent className="w-[400px] sm:w-[540px]">
                    <SheetHeader>
                      <SheetTitle>Select Users to Add</SheetTitle>
                      <SheetDescription>
                        Search and select multiple users to add to the
                        permission grid.
                      </SheetDescription>
                    </SheetHeader>
                    <div className="space-y-4">
                      <div className="relative">
                        <Search className="absolute left-3 top-3 h-4 w-4 text-muted-foreground" />
                        <Input
                          type="text"
                          placeholder="Search by email..."
                          value={newUserSearch}
                          onChange={(e) => setNewUserSearch(e.target.value)}
                          className="pl-10"
                        />
                      </div>

                      {/* User Search Results */}
                      {debouncedNewUserSearch &&
                        debouncedNewUserSearch.length >= 2 && (
                          <div className="space-y-2 max-h-60 overflow-y-auto border rounded p-2">
                            {searchingNewUsers ? (
                              <div className="flex items-center justify-center py-4">
                                <Loader2 className="h-6 w-6 animate-spin" />
                              </div>
                            ) : newUserSearchResults?.users &&
                              newUserSearchResults.users.length > 0 ? (
                              newUserSearchResults.users
                                .filter((user: User) => {
                                  // Filter out users already in the grid
                                  const isInGrid = gridUsers.some(
                                    (gu) => gu.user.id === user.id,
                                  );
                                  // Filter out users already in existing admins/managers
                                  const isExisting =
                                    locationAdminsAndManagers?.some(
                                      (perm) => perm.user.id === user.id,
                                    );
                                  return !isInGrid && !isExisting;
                                })
                                .map((user: User) => (
                                  <div
                                    key={user.id}
                                    className="admin-hoverable flex items-center justify-between p-3 border rounded"
                                  >
                                    <div className="flex items-center space-x-3">
                                      <Checkbox
                                        checked={selectedUsersForFlyout.some(
                                          (u) => u.id === user.id,
                                        )}
                                        onCheckedChange={() =>
                                          toggleUserSelectionInFlyout(user)
                                        }
                                      />
                                      <div>
                                        <p className="font-medium text-sm">
                                          {user.username || user.email}
                                        </p>
                                        <p className="text-xs text-muted-foreground">
                                          {user.email}
                                        </p>
                                      </div>
                                      {user.isAdmin && (
                                        <span className="text-sm font-medium text-secondary">
                                          Global Admin
                                        </span>
                                      )}
                                    </div>
                                  </div>
                                ))
                            ) : (
                              <div className="text-sm text-muted-foreground p-4 text-center">
                                No users found
                              </div>
                            )}
                          </div>
                        )}

                      {/* Selected Users Summary */}
                      {selectedUsersForFlyout.length > 0 && (
                        <div className="border rounded p-3 bg-blue-50 dark:bg-blue-950">
                          <p className="text-sm font-medium mb-2">
                            Selected Users ({selectedUsersForFlyout.length}):
                          </p>
                          <div className="space-y-1">
                            {selectedUsersForFlyout.map((user) => (
                              <div
                                key={user.id}
                                className="flex items-center justify-between text-sm"
                              >
                                <span>{user.username || user.email}</span>
                                <Button
                                  variant="ghost"
                                  size="sm"
                                  onClick={() =>
                                    toggleUserSelectionInFlyout(user)
                                  }
                                >
                                  <X className="h-3 w-3" />
                                </Button>
                              </div>
                            ))}
                          </div>
                        </div>
                      )}

                      <div className="flex justify-end space-x-2">
                        <Button
                          variant="outline"
                          onClick={() => setIsFlyoutDialogOpen(false)}
                        >
                          Cancel
                        </Button>
                        <Button
                          onClick={addSelectedUsersToGrid}
                          disabled={selectedUsersForFlyout.length === 0}
                        >
                          Add {selectedUsersForFlyout.length} User
                          {selectedUsersForFlyout.length !== 1 ? "s" : ""}
                        </Button>
                      </div>
                    </div>
                  </SheetContent>
                </Sheet>
              </div>
            </div>
          </div>
        </div>

        {/* Filter Bar (matches Locations) */}
        {selectedLocation && (
          <TableFilterLayout
            className="fixed top-[calc(var(--header-height)+6rem)] left-[var(--sidebar-width)] right-0 z-20"
            isExpanded={isFilterExpanded}
            columns={[
              { title: "USER", span: 3 },
              { title: "ROLE", span: 2 },
              { title: "FACILITIES", span: 2, align: "center" },
              { title: "NOTICES", span: 2, align: "center" },
              { title: "EVENTS", span: 2, align: "center" },
              { title: "TENANTS", span: 1, align: "center" },
            ]}
            filters={[
              {
                span: 3,
                content: (
                  <div className="relative">
                    <Search className="absolute left-3 top-1/2 -translate-y-1/2 transform text-gray-400 w-4 h-4" />
                    <input
                      type="text"
                      placeholder="Search users by name or email..."
                      value={displaySearch}
                      onChange={(e) => setDisplaySearch(e.target.value)}
                      className="w-full pl-10 pr-3 py-2 bg-white border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    />
                  </div>
                ),
              },
              { span: 9, content: <div className="h-10" /> },
            ]}
          />
        )}

        {/* Scrollable Content */}
        <div
          className={`flex-1 overflow-y-auto transition-[padding] duration-300 ease-in-out ${contentPaddingTop}`}
        >
          <div className="w-full">
            {/* Current Administrators and Managers */}
            {selectedLocation && (
              <div className="w-full">
                {/* Save Button for New Users moved to Header */}

                {loadingUsers ? (
                  <div className="flex items-center justify-center py-8">
                    <Loader2 className="h-6 w-6 animate-spin" />
                  </div>
                ) : locationUsersError ? (
                  <Alert variant="destructive">
                    <AlertDescription>
                      <p className="font-semibold mb-2">Error loading users</p>
                      <p className="text-sm">
                        {locationUsersError?.message ||
                          "Failed to load location administrators and managers"}
                      </p>
                    </AlertDescription>
                  </Alert>
                ) : (locationAdminsAndManagers &&
                  locationAdminsAndManagers.length > 0) ||
                  gridUsers.length > 0 ? (
                  <div className="w-full">
                    <div className="divide-y divide-gray-200">
                      {/* New Users (from flyout) - with inline editing */}
                      {gridUsers
                        .filter((gu) => {
                          const s = displaySearch.toLowerCase();
                          if (!s) return true;
                          const name = gu.user.username || "";
                          const email = gu.user.email || "";
                          return (
                            name.toLowerCase().includes(s) ||
                            email.toLowerCase().includes(s)
                          );
                        })
                        .map((gridUser) => (
                          <div
                            key={`new-${gridUser.user.id}`}
                            className="admin-hoverable px-6 py-3 bg-blue-50 dark:bg-sidebar-active/50 transition-colors"
                            title="Double-click to edit (already in edit mode)"
                          >
                            <div className="grid grid-cols-12 gap-4 items-start">
                              <div className="col-span-3">
                                <div className="text-sm font-medium text-gray-900 dark:text-white">
                                  {gridUser.user.username ||
                                    gridUser.user.email}
                                </div>
                                <div className="text-sm text-gray-500 dark:text-gray-200">
                                  {gridUser.user.email}
                                </div>
                              </div>
                              <div className="col-span-2 flex justify-start">
                                <Select
                                  value={gridUser.role}
                                  onValueChange={(value: "ADMIN" | "MANAGER") =>
                                    updateUserRole(gridUser.user.id, value)
                                  }
                                >
                                  <SelectTrigger className="w-32">
                                    <SelectValue />
                                  </SelectTrigger>
                                  <SelectContent>
                                    <SelectItem value="ADMIN">
                                      Administrator
                                    </SelectItem>
                                    <SelectItem value="MANAGER">
                                      Manager
                                    </SelectItem>
                                  </SelectContent>
                                </Select>
                              </div>
                              <div className="col-span-2 flex justify-center">
                                <Checkbox
                                  checked={gridUser.permissions.FACILITY}
                                  onCheckedChange={(checked) =>
                                    updateUserPermission(
                                      gridUser.user.id,
                                      "FACILITY",
                                      checked as boolean,
                                    )
                                  }
                                  disabled={gridUser.role === "ADMIN"}
                                  className="border-gray-400 data-[state=checked]:bg-blue-600 data-[state=checked]:text-white data-[state=checked]:border-blue-600 [&[data-state=checked]_svg]:!text-white"
                                />
                              </div>
                              <div className="col-span-2 flex justify-center">
                                <Checkbox
                                  checked={gridUser.permissions.NOTICE}
                                  onCheckedChange={(checked) =>
                                    updateUserPermission(
                                      gridUser.user.id,
                                      "NOTICE",
                                      checked as boolean,
                                    )
                                  }
                                  disabled={gridUser.role === "ADMIN"}
                                  className="border-gray-400 data-[state=checked]:bg-blue-600 data-[state=checked]:text-white data-[state=checked]:border-blue-600 [&[data-state=checked]_svg]:!text-white"
                                />
                              </div>
                              <div className="col-span-2 flex justify-center">
                                <Checkbox
                                  checked={gridUser.permissions.EVENT}
                                  onCheckedChange={(checked) =>
                                    updateUserPermission(
                                      gridUser.user.id,
                                      "EVENT",
                                      checked as boolean,
                                    )
                                  }
                                  disabled={gridUser.role === "ADMIN"}
                                  className="border-gray-400 data-[state=checked]:bg-blue-600 data-[state=checked]:text-white data-[state=checked]:border-blue-600 [&[data-state=checked]_svg]:!text-white"
                                />
                              </div>
                              <div className="col-span-1 flex justify-center">
                                <Checkbox
                                  checked={gridUser.permissions.TENANT_BUSINESS}
                                  onCheckedChange={(checked) =>
                                    updateUserPermission(
                                      gridUser.user.id,
                                      "TENANT_BUSINESS",
                                      checked as boolean,
                                    )
                                  }
                                  disabled={gridUser.role === "ADMIN"}
                                  className="border-gray-400 data-[state=checked]:bg-blue-600 data-[state=checked]:text-white data-[state=checked]:border-blue-600 [&[data-state=checked]_svg]:!text-white"
                                />
                              </div>
                            </div>
                          </div>
                        ))}

                      {/* Existing Users (read-only) */}
                      {(() => {
                        // Group permissions by user
                        const userPermissions = new Map();
                        locationAdminsAndManagers.forEach((permission) => {
                          const userId = permission.user.id;
                          if (!userPermissions.has(userId)) {
                            userPermissions.set(userId, {
                              user: permission.user,
                              permissions: new Set(),
                              contentTypes: new Set(),
                            });
                          }
                          userPermissions
                            .get(userId)
                            .contentTypes.add(permission.contentType);
                          permission.permissions.forEach((perm) => {
                            userPermissions.get(userId).permissions.add(perm);
                          });
                        });

                        return Array.from(userPermissions.values())
                          .filter((userData) => {
                            const s = displaySearch.toLowerCase();
                            if (!s) return true;
                            const name = userData.user.username || "";
                            const email = userData.user.email || "";
                            return (
                              name.toLowerCase().includes(s) ||
                              email.toLowerCase().includes(s)
                            );
                          })
                          .map((userData) => {
                            const isAdmin =
                              userData.contentTypes.size === 4 &&
                              userData.permissions.has("APPROVE");
                            const role = isAdmin ? "Administrator" : "Manager";

                            return (
                              <div
                                key={userData.user.id}
                                className="admin-hoverable px-6 py-3 transition-colors cursor-pointer"
                                onClick={() => {
                                  setFlyoutUserId(userData.user.id);
                                  setFlyoutMode("view");
                                }}
                                title="Click to view details"
                              >
                                <div className="grid grid-cols-12 gap-4 items-start">
                                  <div className="col-span-3">
                                    <div className="text-sm font-medium text-gray-900 dark:text-white">
                                      {userData.user.username ||
                                        userData.user.email}
                                    </div>
                                    <div className="text-sm text-gray-500 dark:text-gray-200">
                                      {userData.user.email}
                                    </div>
                                  </div>
                                  <div className="col-span-2 flex justify-start">
                                    <span
                                      className={`text-sm font-medium ${isAdmin
                                          ? "text-primary"
                                          : "text-secondary"
                                        }`}
                                    >
                                      {role}
                                    </span>
                                  </div>
                                  <div className="col-span-2 flex justify-center">
                                    {userData.contentTypes.has("FACILITY") ? (
                                      <div className="h-5 w-5 rounded-full bg-blue-600 flex items-center justify-center">
                                        <CheckCircle className="h-4 w-4 text-white" />
                                      </div>
                                    ) : (
                                      <XCircle className="h-5 w-5 text-gray-300" />
                                    )}
                                  </div>
                                  <div className="col-span-2 flex justify-center">
                                    {userData.contentTypes.has("NOTICE") ? (
                                      <div className="h-5 w-5 rounded-full bg-blue-600 flex items-center justify-center">
                                        <CheckCircle className="h-4 w-4 text-white" />
                                      </div>
                                    ) : (
                                      <XCircle className="h-5 w-5 text-gray-300" />
                                    )}
                                  </div>
                                  <div className="col-span-2 flex justify-center">
                                    {userData.contentTypes.has("EVENT") ? (
                                      <div className="h-5 w-5 rounded-full bg-blue-600 flex items-center justify-center">
                                        <CheckCircle className="h-4 w-4 text-white" />
                                      </div>
                                    ) : (
                                      <XCircle className="h-5 w-5 text-gray-300" />
                                    )}
                                  </div>
                                  <div className="col-span-1 flex justify-center">
                                    {userData.contentTypes.has(
                                      "TENANT_BUSINESS",
                                    ) ? (
                                      <div className="h-5 w-5 rounded-full bg-blue-600 flex items-center justify-center">
                                        <CheckCircle className="h-4 w-4 text-white" />
                                      </div>
                                    ) : (
                                      <XCircle className="h-5 w-5 text-gray-300" />
                                    )}
                                  </div>
                                </div>
                              </div>
                            );
                          });
                      })()}
                    </div>
                  </div>
                ) : (
                  <div className="flex items-center justify-center min-h-[60vh] px-4 py-12">
                    <Alert className="max-w-md">
                      <AlertDescription>
                        {gridUsers.length > 0 ? (
                          <div>
                            <p className="mb-2">
                              No existing users with permissions. You have{" "}
                              {gridUsers.length} user(s) ready to save.
                            </p>
                            <p className="text-sm text-muted-foreground">
                              Select their roles and permissions above, then
                              click Save.
                            </p>
                          </div>
                        ) : (
                          "No users have administrator or manager permissions for this location. Click 'Add User' to assign permissions."
                        )}
                      </AlertDescription>
                    </Alert>
                  </div>
                )}
              </div>
            )}

            {/* Empty State - No Location Selected */}
            {!selectedLocation && uniqueLocations.length > 0 && (
              <div className="flex items-center justify-center py-12">
                <div className="text-center">
                  <Building className="h-12 w-12 text-gray-400 mx-auto mb-4" />
                  <h3 className="text-lg font-semibold text-gray-900 mb-2">
                    No Location Selected
                  </h3>
                  <p className="text-gray-600 max-w-md">
                    Please select a location from the dropdown above to manage
                    permissions for users.
                  </p>
                </div>
              </div>
            )}

            {/* Empty State - No Locations Available */}
            {!selectedLocation && uniqueLocations.length === 0 && (
              <div className="flex items-center justify-center py-12">
                <div className="text-center max-w-md">
                  <Shield className="h-12 w-12 text-gray-400 mx-auto mb-4" />
                  <h3 className="text-lg font-semibold text-gray-900 mb-2">
                    No Locations Available
                  </h3>
                  <p className="text-gray-600 mb-4">
                    {user?.isAdmin
                      ? "You have access to manage all locations. Please wait while locations are loading, or refresh the page."
                      : "You need to be a location administrator to manage permissions. Please contact a global administrator to assign you as a location administrator for at least one location."}
                  </p>
                  <Button
                    variant="outline"
                    onClick={() => window.location.reload()}
                  >
                    Refresh Page
                  </Button>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Flyout Pane for User Details */}
      <AdminFlyoutPane
        open={!!flyoutUserId}
        onOpenChange={(open) => {
          if (!open) {
            setFlyoutUserId(null);
            setFlyoutMode("view");
          }
        }}
        title={
          flyoutMode === "view"
            ? "User Permission Details"
            : "Edit User Permissions"
        }
        description={
          availableLocations.find((loc: any) => loc.id === selectedLocation)
            ?.name || "Current Location"
        }
        width="2xl"
        mode={flyoutMode}
        onNavigatePrevious={handleNavigatePreviousUser}
        onNavigateNext={handleNavigateNextUser}
        hasPrevious={hasPreviousUser}
        hasNext={hasNextUser}
        headerActions={
          flyoutMode === "view" ? (
            <>
              <Button
                variant="destructive"
                size="sm"
                onClick={() => {
                  if (
                    window.confirm(
                      "Are you sure you want to revoke all permissions for this user?",
                    )
                  ) {
                    handleRevokePermissions(flyoutUserId!);
                    setFlyoutUserId(null);
                  }
                }}
                className="bg-red-600 hover:bg-red-700 text-white w-[110px]"
              >
                Revoke
              </Button>
              <Button
                onClick={() => {
                  setFlyoutMode("edit");
                  const userData = locationAdminsAndManagers?.find(
                    (perm: any) => perm.user.id === flyoutUserId,
                  );
                  if (userData) {
                    const userPerms = locationAdminsAndManagers
                      ?.filter((p: any) => p.user.id === flyoutUserId)
                      .reduce(
                        (acc: any, perm: any) => {
                          if (!acc.contentTypes) acc.contentTypes = new Set();
                          if (!acc.permissions) acc.permissions = new Set();
                          acc.contentTypes.add(perm.contentType);
                          perm.permissions.forEach((p: string) =>
                            acc.permissions.add(p),
                          );
                          return acc;
                        },
                        { user: userData.user },
                      );
                    const isAdmin =
                      userPerms?.contentTypes?.size === 4 &&
                      userPerms?.permissions?.has("APPROVE");
                    setEditingUserData({
                      role: isAdmin ? "ADMIN" : "MANAGER",
                      permissions: {
                        FACILITY:
                          userPerms?.contentTypes?.has("FACILITY") || false,
                        NOTICE: userPerms?.contentTypes?.has("NOTICE") || false,
                        EVENT: userPerms?.contentTypes?.has("EVENT") || false,
                        TENANT_BUSINESS:
                          userPerms?.contentTypes?.has("TENANT_BUSINESS") ||
                          false,
                      },
                    });
                  }
                }}
                size="sm"
                className="w-[110px]"
              >
                Edit
              </Button>
            </>
          ) : (
            <>
              <Button
                variant="outline"
                size="sm"
                onClick={() => {
                  setFlyoutMode("view");
                  setEditingUserData(null);
                }}
                className="w-[110px]"
              >
                Close
              </Button>
              <Button
                onClick={async () => {
                  if (!editingUserData) return;
                  setIsSaving(true);
                  try {
                    if (editingUserData.role === "ADMIN") {
                      for (const contentType of CONTENT_TYPES) {
                        await grantPermissionsAction({
                          userId: flyoutUserId!,
                          locationId: selectedLocation,
                          contentType: contentType as any,
                          permissions: PERMISSION_TYPES as any,
                        });
                      }
                    } else {
                      for (const contentType of CONTENT_TYPES) {
                        try {
                          await revokePermissionsAction({
                            userId: flyoutUserId!,
                            locationId: selectedLocation,
                            contentType: contentType as any,
                          });
                        } catch (err) { }
                      }
                      for (const [contentType, hasPermission] of Object.entries(
                        editingUserData.permissions,
                      )) {
                        if (hasPermission) {
                          await grantPermissionsAction({
                            userId: flyoutUserId!,
                            locationId: selectedLocation,
                            contentType: contentType as any,
                            permissions: PERMISSION_TYPES as any,
                          });
                        }
                      }
                    }
                    refetchUsers();
                    setFlyoutMode("view");
                    setEditingUserData(null);
                    toast({
                      title: "Success",
                      description: "User permissions updated successfully",
                    });
                  } catch (error) {
                    console.error("Error updating user permissions:", error);
                    toast({
                      title: "Error",
                      description: "Failed to update user permissions",
                      variant: "destructive",
                    });
                  } finally {
                    setIsSaving(false);
                  }
                }}
                disabled={isSaving}
                size="sm"
                className="w-[110px]"
              >
                {isSaving ? (
                  <>
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                    Saving...
                  </>
                ) : (
                  <>
                    <Save className="w-4 h-4 mr-2" />
                    Save
                  </>
                )}
              </Button>
            </>
          )
        }
      >
        {(() => {
          const userData = locationAdminsAndManagers?.find(
            (perm: any) => perm.user.id === flyoutUserId,
          );
          if (!userData) return null;

          // Group permissions by user
          const userPerms = locationAdminsAndManagers
            ?.filter((p: any) => p.user.id === flyoutUserId)
            .reduce(
              (acc: any, perm: any) => {
                if (!acc.contentTypes) acc.contentTypes = new Set();
                if (!acc.permissions) acc.permissions = new Set();
                acc.contentTypes.add(perm.contentType);
                perm.permissions.forEach((p: string) => acc.permissions.add(p));
                return acc;
              },
              { user: userData.user },
            );

          const isAdmin =
            userPerms?.contentTypes?.size === 4 &&
            userPerms?.permissions?.has("APPROVE");
          const role = isAdmin ? "Administrator" : "Manager";

          return (
            <>
              {/* Header Section */}
              <FlyoutSection>
                <div className="space-y-1">
                  <p className="text-sm font-medium text-gray-900 dark:text-white">
                    {userData.user.username || userData.user.email}
                  </p>
                  <p className="text-sm text-gray-500 dark:text-gray-400">
                    {userData.user.email}
                  </p>
                </div>
              </FlyoutSection>

              {/* Role Information - View and Edit Combined */}
              <FlyoutSection title="Role">
                {flyoutMode === "edit" ? (
                  <Select
                    value={editingUserData?.role || "MANAGER"}
                    onValueChange={(value: "ADMIN" | "MANAGER") => {
                      setEditingUserData((prev) =>
                        prev
                          ? {
                            ...prev,
                            role: value,
                            permissions:
                              value === "ADMIN"
                                ? {
                                  FACILITY: true,
                                  NOTICE: true,
                                  EVENT: true,
                                  TENANT_BUSINESS: true,
                                }
                                : prev.permissions,
                          }
                          : null,
                      );
                    }}
                  >
                    <SelectTrigger className="w-full">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="ADMIN">Administrator</SelectItem>
                      <SelectItem value="MANAGER">Manager</SelectItem>
                    </SelectContent>
                  </Select>
                ) : (
                  <div className="p-3 bg-gray-50 dark:bg-gray-800 rounded-lg">
                    <span
                      className={`text-lg font-semibold ${isAdmin ? "text-primary" : "text-secondary"
                        }`}
                    >
                      {role}
                    </span>
                    <p className="text-sm text-gray-500 mt-1">
                      {isAdmin
                        ? "Full access to all content types and permissions"
                        : "Limited access to specific content types"}
                    </p>
                  </div>
                )}
              </FlyoutSection>

              {/* Permissions Breakdown - View and Edit Combined */}
              <FlyoutSection title="Content Type Permissions">
                <div className="space-y-3">
                  {["FACILITY", "NOTICE", "EVENT", "TENANT_BUSINESS"].map(
                    (contentType) => (
                      <div
                        key={contentType}
                        className="flex items-center justify-between p-3 bg-gray-50 dark:bg-gray-800 rounded-lg"
                      >
                        <span className="text-sm font-medium">
                          {contentType.replace("_", " ")}
                        </span>
                        {flyoutMode === "edit" ? (
                          <Checkbox
                            checked={
                              editingUserData?.permissions[
                              contentType as keyof typeof editingUserData.permissions
                              ] || false
                            }
                            onCheckedChange={(checked) => {
                              setEditingUserData((prev) =>
                                prev
                                  ? {
                                    ...prev,
                                    permissions: {
                                      ...prev.permissions,
                                      [contentType]: checked as boolean,
                                    },
                                  }
                                  : null,
                              );
                            }}
                            disabled={editingUserData?.role === "ADMIN"}
                            className="border-gray-400 data-[state=checked]:bg-blue-600 data-[state=checked]:text-white data-[state=checked]:border-blue-600 [&[data-state=checked]_svg]:!text-white"
                          />
                        ) : (
                          <>
                            {userPerms?.contentTypes?.has(contentType) ? (
                              <div className="flex items-center gap-2">
                                <CheckCircle className="w-5 h-5 text-green-500" />
                                <span className="text-sm text-green-600 font-medium">
                                  Granted
                                </span>
                              </div>
                            ) : (
                              <div className="flex items-center gap-2">
                                <XCircle className="w-5 h-5 text-gray-300" />
                                <span className="text-sm text-gray-400">
                                  No Access
                                </span>
                              </div>
                            )}
                          </>
                        )}
                      </div>
                    ),
                  )}
                </div>
              </FlyoutSection>
            </>
          );
        })()}
      </AdminFlyoutPane>
    </DefaultLayout>
  );
}
